import { useI18n } from 'context/i18n';
import { Dialog, DialogHeader, DialogRoot } from './ui/dialog';
import { Button } from './ui/button';
import { useLocale } from 'hooks';
import { useEffect, useState } from 'react';
import { storage } from 'utils/web-storage';
import type { TCountry } from 'actions/global';
import { setGBCookie } from 'utils/i18n';
import { useCountries } from 'hooks/useCountries';
import { useRouter } from 'next/router';
import { getSlug } from 'actions/slug';
import { COOKIES, setCookie } from 'utils/helpers';

export type GeoDetectionStorage = {
  open: boolean;
  newsletter: null | Parameters<typeof window.openNewsletterModal>;
  membersOnly: null | Parameters<typeof window.showMembersOnlyModal>;
};

export function CountryDetectionModal({ detectedCountry, translateSlug }: { detectedCountry: string; translateSlug: boolean }) {
  const { asPath } = useRouter();
  const { countries } = useI18n();
  const { country, language: defaultLanguage } = useLocale();
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(true);
  const { isBritishCountry } = useCountries();
  const defaultCountryData = countries.find((c) => c.id === country);
  const detectedCountryData = countries.find((c) => c.id === detectedCountry);

  useEffect(() => {
    if (isOpen) {
      storage.session.set('geo_detection', {
        open: true,
        newsletter: null,
        membersOnly: null,
      });
    }
  }, [isOpen]);

  const onClose = (isOpen: boolean, skip = false) => {
    if (!isOpen) {
      const geoDetection = storage.session.get<GeoDetectionStorage>('geo_detection');
      if (geoDetection && !skip) {
        storage.session.remove('geo_detection');
        if (geoDetection.newsletter !== null && geoDetection.membersOnly !== null) {
          window.openNewsletterModal?.(...geoDetection.newsletter);
        }
      }
      setCookie(COOKIES.ALREADY_DETECTED, true, 7, COOKIES.DOMAIN);
      setIsOpen(false);
    }
  };

  const onChangeCountry = async (country: TCountry) => {
    if (isBritishCountry(country.id)) {
      setGBCookie(country.id);
    }
    const languageTarget = country.languages.find((l) => l.default)?.id ?? country.languages[0]?.id ?? 'en';

    let redirect = asPath;
    if (translateSlug) {
      redirect = await getSlug({ lang: defaultLanguage, targetLanguage: languageTarget, slug: asPath });
    }
    window.location.assign(`${window.location.origin}/${languageTarget}_${country.id}${redirect}`);
    onClose(false, true);
  };

  return (
    <DialogRoot
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        onClose(isOpen);
      }}
      showOverlay
    >
      <Dialog>
        <DialogHeader label={t('modal.idiomas', 'choose.country.geoip')} hidden />
        <div className="mt-5 flex flex-col gap-10 px-4 pb-4 lab:mt-4 lab:gap-2 lab:px-0 lab:pb-0">
          <p aria-hidden className="mb-0 text-center text-2xl lab:text-left lab:text-sm lab:font-medium lab:lg:text-xs">
            {t('modal.idiomas', 'choose.country.geoip')}
          </p>
          <div className="grid grid-cols-2 gap-1">
            <Button variant="outline" onPress={() => onClose(false)}>
              {t('modal.idiomas', 'stay', '***Stay')} {defaultCountryData?.name}
            </Button>
            <Button variant="outline" onPress={() => onChangeCountry(detectedCountryData)}>
              {t('modal.idiomas', 'go.to', '***Go to')} {detectedCountryData?.name}
            </Button>
          </div>
        </div>
      </Dialog>
    </DialogRoot>
  );
}
